import lodash from "lodash";
import { Decoration_Type, Node_Type, RichContent } from "./../../../../ricos-schema/legacy/wix/rich_content/v1/index.js";
import { removeInlineHeaderRanges } from "../../../normalization/removeInlineHeaderRanges.js";
import { getValidHexColor } from "../../../utils.js";
import { generateId } from "../../generateRandomId.js";
import { createParagraphNode, initializeMetadata } from "../../nodeUtils.js";
import { BlockType, FROM_DRAFT_LIST_TYPE, HeaderLevel } from "../consts.js";
import { getEntity, getNodeStyle, getTextStyle } from "./getRicosEntityData.js";
import { getTextNodes } from "./getTextNodes.js";
import { parseDocStyle } from "./parse-doc-style.js";
const ensureRicosContent = (content, opts) => "blocks" in content ? fromDraft(content, opts) : content;
const parseCollapsible = (entity, opts) => entity.pairs.map((pair) => ({
  id: pair.key,
  type: Node_Type.COLLAPSIBLE_ITEM,
  nodes: [
    {
      id: generateId(),
      type: Node_Type.COLLAPSIBLE_ITEM_TITLE,
      nodes: fromDraft(pair.title, opts).nodes
    },
    {
      id: generateId(),
      type: Node_Type.COLLAPSIBLE_ITEM_BODY,
      nodes: fromDraft(pair.content, opts).nodes
    }
  ]
}));
const parseTable = (entity, opts) => Object.entries(entity.rows).map(([, row]) => ({
  id: generateId(),
  type: Node_Type.TABLE_ROW,
  nodes: Object.entries(row.columns ?? {}).map(([, cell]) => ({
    id: generateId(),
    type: Node_Type.TABLE_CELL,
    tableCellData: {
      cellStyle: {
        verticalAlignment: cell.style?.verticalAlign?.toUpperCase(),
        backgroundColor: getValidHexColor(cell.style?.backgroundColor)?.toUpperCase()
      },
      borderColors: {
        top: getValidHexColor(cell.border?.top)?.toUpperCase(),
        left: getValidHexColor(cell.border?.left)?.toUpperCase(),
        right: getValidHexColor(cell.border?.right)?.toUpperCase(),
        bottom: getValidHexColor(cell.border?.bottom)?.toUpperCase()
      }
    },
    nodes: fromDraft(cell.content, opts).nodes
  }))
}));
const nestedNodesConverters = {
  [Node_Type.COLLAPSIBLE_LIST]: parseCollapsible,
  [Node_Type.TABLE]: parseTable
};
const normalizeBlock = (block) => {
  if (block.type === "atomic" && block.text.startsWith(" ") && block.text.trim() !== "" && block.text?.length > 1) {
    block.type = "unstyled";
  }
  block.depth = block.depth || 0;
  block.entityRanges = block.entityRanges || [];
  block.inlineStyleRanges = (block.inlineStyleRanges || []).filter((range) => range.offset >= 0).map((range) => {
    if (range.style.startsWith("#")) {
      return {
        ...range,
        style: `{"FG":"${range.style}"}`
      };
    }
    try {
      const parsedStyle = JSON.parse(range.style);
      if ("font-size" in parsedStyle) {
        const [value, unit] = parsedStyle["font-size"].split(/(px|em)/gi);
        return {
          ...range,
          style: `{"font-size":"${parseInt(value, 10)}${unit}"}`
        };
      }
    } catch {
    }
    return range;
  });
};
const fromDraft = (draftJSON, opts = {}) => {
  const { blocks = [], entityMap = {}, documentStyle, ID: id } = lodash.cloneDeep(draftJSON);
  const nodes = [];
  const contentIdPrefix = Math.random().toString(36).substr(2, 9);
  const usedKeys = {};
  const withDepth = (block, depth = 0) => ({ ...block, depth });
  const ensureUniqueKey = (key) => {
    if (usedKeys[key]) {
      console.warn(`WARN! Duplicate block key "${key}"!`);
      key = generateId();
    }
    usedKeys[key] = true;
    return key;
  };
  const parseBlock = (index) => {
    let block = blocks[index];
    if (!block) {
      return -1;
    }
    block = removeInlineHeaderRanges(block);
    normalizeBlock(block);
    switch (block.type) {
      case BlockType.Atomic:
        const atomicBlocks = parseAtomicBlock(block);
        atomicBlocks?.forEach((atomicBlock) => {
          nodes.push(atomicBlock);
        });
        return index + 1;
      case BlockType.Blockquote:
        nodes.push(parseQuoteBlock(block));
        return index + 1;
      case BlockType.CodeBlock:
        nodes.push(parseCodeBlock(block));
        return index + 1;
      case BlockType.HeaderOne:
      case BlockType.HeaderTwo:
      case BlockType.HeaderThree:
      case BlockType.HeaderFour:
      case BlockType.HeaderFive:
      case BlockType.HeaderSix:
        nodes.push(parseHeadingBlock(block));
        return index + 1;
      case BlockType.OrderedListItem:
      case BlockType.UnorderedListItem:
        const { node, nextIndex } = parseListBlocks(index);
        nodes.push(node);
        return nextIndex;
      case void 0:
      case BlockType.Unstyled:
        nodes.push(parseTextBlock(block));
        return index + 1;
      default:
        if (opts.ignoreUnsupportedValues) {
          return index + 1;
        } else {
          throw Error(`ERROR! Unknown block type "${block.type}"!`);
        }
    }
  };
  const parseAtomicBlock = (block) => {
    const { key, data, entityRanges } = block;
    if (entityRanges && entityRanges.length) {
      return entityRanges.map((entityRange) => {
        const entity = getEntity(entityRange.key, entityMap);
        if (entity && entity.type) {
          switch (entity.type) {
            case Decoration_Type.LINK:
            case Decoration_Type.ANCHOR:
            case Decoration_Type.MENTION: {
              return parseTextBlock(block);
            }
            default: {
              const nodes2 = nestedNodesConverters[entity.type]?.(
                entity,
                opts
              ) || [];
              if (!nodes2.length && (entity.type === Node_Type.TABLE || entity.type === Node_Type.COLLAPSIBLE_LIST)) {
                return null;
              }
              const shouldAddStyle = ![
                Node_Type.IMAGE,
                Node_Type.VIDEO,
                Node_Type.DIVIDER,
                Node_Type.HTML,
                Node_Type.GALLERY,
                Node_Type.BUTTON,
                Node_Type.FILE,
                Node_Type.COLLAPSIBLE_LIST,
                Node_Type.GIF,
                Node_Type.TABLE,
                Node_Type.APP_EMBED,
                Node_Type.POLL
              ].includes(entity.type);
              return {
                id: ensureUniqueKey(key),
                nodes: nodes2,
                ...shouldAddStyle ? { style: getNodeStyle(data) } : {},
                ...entity
              };
            }
          }
        }
        return null;
      }).filter(isValidNode);
    }
    return null;
  };
  const isValidNode = (node) => node !== null;
  const parseQuoteBlock = (block) => ({
    id: ensureUniqueKey(block.key),
    type: Node_Type.BLOCKQUOTE,
    nodes: [parseTextBlock(withDepth(block))],
    style: getNodeStyle(block.data),
    blockquoteData: { indentation: block.depth }
  });
  const parseCodeBlock = (block) => ({
    id: ensureUniqueKey(block.key),
    type: Node_Type.CODE_BLOCK,
    nodes: getTextNodes(block, entityMap, opts, false),
    style: getNodeStyle(block.data),
    codeBlockData: {
      textStyle: getTextStyle(block.data)
    }
  });
  const parseHeadingBlock = (block) => {
    const getLevel = (blockType) => {
      if (Object.keys(HeaderLevel).includes(blockType)) {
        return HeaderLevel[blockType];
      }
      throw Error(`ERROR! Unknown header level "${blockType}"!`);
    };
    return {
      id: ensureUniqueKey(block.key),
      type: Node_Type.HEADING,
      headingData: {
        level: getLevel(block.type),
        indentation: block.depth,
        textStyle: getTextStyle(block.data)
      },
      nodes: getTextNodes(block, entityMap, opts),
      style: getNodeStyle(block.data)
    };
  };
  const parseTextBlock = (block) => {
    const paragraphNode = createParagraphNode(
      [],
      { textStyle: getTextStyle(block.data) },
      getNodeStyle(block.data)
    );
    switch (block.type) {
      case BlockType.Unstyled:
        paragraphNode.id = ensureUniqueKey(block.key);
      // falls through
      case void 0:
      case BlockType.Atomic:
      case BlockType.Blockquote:
        if (paragraphNode.paragraphData) {
          paragraphNode.paragraphData.indentation = parseDepth(block.depth);
        }
        break;
      case BlockType.OrderedListItem:
      case BlockType.UnorderedListItem:
        if (paragraphNode.paragraphData) {
          paragraphNode.paragraphData.indentation = parseDepth(block.depth);
        }
        paragraphNode.id = ensureUniqueKey(block.key);
        break;
      default:
    }
    const nodes2 = getTextNodes(block, entityMap, opts);
    if (!lodash.isEmpty(nodes2)) {
      paragraphNode.nodes = nodes2;
    }
    return paragraphNode;
  };
  const createListItem = (block) => ({
    id: generateId(contentIdPrefix),
    type: Node_Type.LIST_ITEM,
    nodes: [parseTextBlock(withDepth(block))]
  });
  const isListBlock = (block) => FROM_DRAFT_LIST_TYPE[block.type] !== void 0;
  const parseListBlocks = (listStartIndex) => {
    const { type: listType, depth } = blocks[listStartIndex];
    const listNodes = [];
    let searchIndex = listStartIndex;
    let nextBlock = blocks[searchIndex];
    while (nextBlock && isListBlock(nextBlock) && nextBlock.depth >= depth) {
      normalizeBlock(nextBlock);
      if (nextBlock.depth > depth) {
        const { node, nextIndex } = parseListBlocks(searchIndex);
        const prevNode = listNodes[listNodes.length - 1];
        if (prevNode.nodes) {
          prevNode.nodes.push(node);
        } else {
          prevNode.nodes = [node];
        }
        searchIndex = nextIndex;
      } else if (nextBlock.type !== listType) {
        break;
      } else {
        listNodes.push(createListItem(nextBlock));
        searchIndex++;
      }
      nextBlock = blocks[searchIndex];
    }
    const listData = FROM_DRAFT_LIST_TYPE[listType] === Node_Type.ORDERED_LIST ? "orderedListData" : "bulletedListData";
    return {
      node: {
        id: generateId(contentIdPrefix),
        type: FROM_DRAFT_LIST_TYPE[listType],
        nodes: listNodes,
        [listData]: { indentation: depth }
      },
      nextIndex: searchIndex
    };
  };
  let i = 0;
  while (i !== -1) {
    i = parseBlock(i);
  }
  const content = {
    nodes,
    metadata: initializeMetadata({ id }),
    documentStyle: documentStyle && parseDocStyle(documentStyle)
  };
  return RichContent.fromJSON(content);
};
const parseDepth = (depth) => {
  if (isNaN(parseInt(depth.toString(), 10))) {
    return 0;
  }
  return parseInt(depth.toString(), 10);
};
export {
  ensureRicosContent,
  fromDraft
};
//# sourceMappingURL=fromDraft.js.map
